import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, BadgeCheck, Search, IndianRupee, Eye, Share2, ShoppingCart, Plus, Minus, X, Trash2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cartKey = (personalId) => `indivalife.cart.${personalId}`;

function useCart(personalId) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cartKey(personalId)) || '[]'); }
    catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem(cartKey(personalId), JSON.stringify(items));
  }, [items, personalId]);
  const add = (deal) => {
    setItems(prev => {
      const i = prev.findIndex(x => x.deal_id === deal.deal_id);
      if (i >= 0) {
        const copy = [...prev];
        copy[i] = { ...copy[i], qty: (copy[i].qty || 1) + 1 };
        return copy;
      }
      return [...prev, {
        deal_id: deal.deal_id, slug: deal.slug, title: deal.title,
        image: deal.images?.[0], price: deal.offer_price, mrp: deal.mrp, qty: 1,
      }];
    });
  };
  const setQty = (deal_id, qty) => setItems(prev => prev.map(x => x.deal_id === deal_id ? { ...x, qty: Math.max(1, qty) } : x));
  const remove = (deal_id) => setItems(prev => prev.filter(x => x.deal_id !== deal_id));
  const clear = () => setItems([]);
  const count = items.reduce((s, x) => s + (x.qty || 1), 0);
  const total = items.reduce((s, x) => s + (x.price || 0) * (x.qty || 1), 0);
  return { items, add, setQty, remove, clear, count, total };
}

export default function BrandStore() {
  const { personalId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');
  const [cartOpen, setCartOpen] = useState(false);
  const [checkout, setCheckout] = useState({ open: false, busy: false, name: '', phone: '', address: '', note: '' });
  const cart = useCart(personalId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API_BASE}/storefront/brand/${personalId}`);
        if (!cancelled) {
          setData(d);
          document.title = `${d.brand.brand_name} · INDIVALIFE Store`;
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? 'not_found' : 'error');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [personalId]);

  const accent = data?.brand?.brand_color || '#1D4ED8';

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.deals.filter(d => {
      if (category !== 'all' && d.category !== category) return false;
      if (q && !(`${d.title} ${d.description} ${d.brand_name || ''}`.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [data, q, category]);

  if (loading) return <div className="min-h-screen grid place-items-center text-sm">Loading store…</div>;
  if (error === 'not_found') return (
    <div className="min-h-screen grid place-items-center px-6 text-center">
      <div>
        <div className="text-6xl mb-3">🛍️</div>
        <h1 className="text-2xl font-black">Brand not found</h1>
        <p className="text-sm text-gray-500 mt-2">This brand may have no active products or the ID is incorrect.</p>
        <a href="/" className="inline-block mt-4 text-[#1E3A8A] underline">Go to INDIVALIFE</a>
      </div>
    </div>
  );
  if (error || !data) return <div className="min-h-screen grid place-items-center">Something went wrong.</div>;

  const { brand, deals } = data;
  const url = window.location.href;
  const categories = Array.from(new Set(deals.map(d => d.category).filter(Boolean)));

  const onShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: brand.brand_name, text: `Check out ${brand.brand_name} on INDIVALIFE`, url }); return; } catch (_e) { /* ignore */ }
    }
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  const placeOrders = async () => {
    if (!checkout.name.trim() || !checkout.phone.trim()) { toast.error('Name + phone required'); return; }
    if (cart.items.length === 0) { toast.error('Cart is empty'); return; }
    const params = new URLSearchParams(window.location.search);
    const partner_code = params.get('partner') || undefined;
    setCheckout(c => ({ ...c, busy: true }));
    try {
      const orderIds = [];
      for (const it of cart.items) {
        const { data } = await axios.post(`${API_BASE}/storefront/order`, {
          deal_id: it.deal_id, slug: it.slug,
          customer_name: checkout.name, customer_phone: checkout.phone,
          customer_address: checkout.address, note: checkout.note,
          quantity: it.qty, partner_code,
        });
        orderIds.push(data.order_id);
      }
      toast.success(`${orderIds.length} order(s) placed — the dealer will contact you on ${checkout.phone}`);
      cart.clear();
      setCheckout({ open: false, busy: false, name: '', phone: '', address: '', note: '' });
      setCartOpen(false);
    } catch (e) {
      const code = e?.response?.data?.detail;
      const msg = code === 'out_of_stock' ? 'One of your items is now out of stock. Please remove it from cart and try again.' : (e?.response?.data?.detail || 'Order failed');
      toast.error(msg);
      setCheckout(c => ({ ...c, busy: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Toaster position="top-right" />

      {/* Floating cart button */}
      <button
        onClick={() => setCartOpen(true)}
        className="fixed bottom-6 right-6 z-30 h-14 px-5 rounded-full text-white font-bold shadow-2xl flex items-center gap-2 active:scale-95 transition-transform"
        style={{ background: accent }}
        data-testid="brand-cart-fab"
      >
        <ShoppingCart className="h-5 w-5" />
        <span>Cart</span>
        {cart.count > 0 && (
          <span className="bg-white text-xs h-6 min-w-6 px-1.5 rounded-full grid place-items-center font-black" style={{ color: accent }} data-testid="brand-cart-count">{cart.count}</span>
        )}
      </button>

      {/* Hero header */}
      <div className="text-white" style={{ background: `linear-gradient(135deg, ${accent}, #0F172A)` }} data-testid="brand-hero">
        <div className="max-w-screen-lg mx-auto px-5 sm:px-8 py-8 sm:py-12">
          <div className="flex items-start sm:items-center gap-4 sm:gap-6 flex-col sm:flex-row">
            {brand.brand_logo_url ? (
              <img src={brand.brand_logo_url} alt={brand.brand_name} className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-white p-1.5 object-contain shadow-2xl" data-testid="brand-logo" />
            ) : (
              <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-white text-3xl font-black grid place-items-center shadow-2xl" style={{ color: accent }}>
                {(brand.brand_name || '?').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] opacity-80 font-bold flex items-center gap-1.5">
                <BadgeCheck className="h-3 w-3" /> INDIVALIFE Verified Brand
              </div>
              <h1 className="text-3xl sm:text-5xl font-black leading-tight mt-1 truncate" data-testid="brand-name">{brand.brand_name}</h1>
              {brand.brand_tagline && <p className="text-sm sm:text-base opacity-90 mt-1">{brand.brand_tagline}</p>}
              <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
                {brand.town && (<span className="inline-flex items-center gap-1 bg-white/15 px-2.5 py-1 rounded-full"><MapPin className="h-3 w-3" />{brand.town}</span>)}
                {brand.personal_id && (<span className="inline-flex items-center gap-1 bg-white/15 px-2.5 py-1 rounded-full font-mono">{brand.personal_id}</span>)}
                <span className="inline-flex items-center gap-1 bg-white/15 px-2.5 py-1 rounded-full">{data.total_deals} products</span>
              </div>
            </div>
            <button onClick={onShare} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center gap-2 text-sm self-stretch sm:self-auto" data-testid="brand-share-btn">
              <Share2 className="h-4 w-4" />Share store
            </button>
          </div>
        </div>
      </div>

      {/* Search + filter bar */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm" data-testid="brand-filter-bar">
        <div className="max-w-screen-lg mx-auto px-5 sm:px-8 py-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2 flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search products…" className="bg-transparent outline-none text-sm flex-1" data-testid="brand-search" />
          </div>
          <div className="flex gap-1.5 overflow-x-auto">
            <button onClick={() => setCategory('all')} className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${category === 'all' ? 'text-white' : 'bg-gray-100 text-gray-700'}`} style={category === 'all' ? { background: accent } : {}} data-testid="filter-all">All ({deals.length})</button>
            {categories.map(c => (
              <button key={c} onClick={() => setCategory(c)} className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap capitalize ${category === c ? 'text-white' : 'bg-gray-100 text-gray-700'}`} style={category === c ? { background: accent } : {}} data-testid={`filter-${c}`}>{c}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Product grid */}
      <div className="max-w-screen-lg mx-auto px-5 sm:px-8 py-6 pb-32">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <div className="text-5xl mb-3">🔎</div>
            <p>No products match your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="brand-product-grid">
            {filtered.map(d => {
              const discountPct = d.mrp ? Math.round(((d.mrp - d.offer_price) / d.mrp) * 100) : 0;
              const out = d.in_stock === false;
              return (
                <div key={d.deal_id} className="group bg-white rounded-xl overflow-hidden border hover:shadow-xl transition relative" data-testid={`product-card-${d.slug}`}>
                  <Link to={`/s/${d.slug}`} className="block">
                    <div className="aspect-square bg-gray-50 relative overflow-hidden">
                      {d.images?.[0] ? (
                        <img src={d.images[0]} alt={d.title} className={`w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300 ${out ? 'opacity-40 grayscale' : ''}`} />
                      ) : (
                        <div className="h-full grid place-items-center text-gray-300 text-4xl">📦</div>
                      )}
                      {discountPct > 0 && !out && (
                        <span className="absolute top-2 left-2 text-[10px] font-black text-white px-2 py-0.5 rounded" style={{ background: accent }}>-{discountPct}%</span>
                      )}
                      {out && (
                        <span className="absolute top-2 left-2 text-[10px] font-black text-white px-2 py-0.5 rounded bg-red-500" data-testid={`out-of-stock-badge-${d.slug}`}>OUT OF STOCK</span>
                      )}
                      {!out && (
                        <span className="absolute top-2 right-2 text-[9px] font-bold text-white px-1.5 py-0.5 rounded bg-blue-500" data-testid={`in-stock-badge-${d.slug}`}>IN STOCK</span>
                      )}
                      {d.views > 5 && !out && (
                        <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"><Eye className="h-2.5 w-2.5" />{d.views}</span>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-bold leading-tight line-clamp-2 min-h-[2.5rem]">{d.title}</h3>
                      <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">{d.description}</p>
                      <div className="flex items-baseline gap-1.5 mt-2 flex-wrap">
                        <div className="text-base font-black flex items-center" style={{ color: accent }}>
                          <IndianRupee className="h-3.5 w-3.5" />{d.offer_price}
                        </div>
                        {d.mrp > d.offer_price && (<div className="text-xs text-gray-400 line-through">₹{d.mrp}</div>)}
                      </div>
                    </div>
                  </Link>
                  <button
                    disabled={out}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); cart.add(d); toast.success(`${d.title} added to cart`); }}
                    className={`w-full py-2.5 text-sm font-bold flex items-center justify-center gap-1.5 border-t transition ${out ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'text-white hover:opacity-90'}`}
                    style={out ? {} : { background: accent }}
                    data-testid={`add-to-cart-${d.slug}`}
                  >
                    {out ? 'Unavailable' : (<><Plus className="h-3.5 w-3.5" />Add to cart</>)}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {brand.address && (
          <div className="mt-10 p-5 bg-white rounded-xl border">
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Visit our store</div>
            <div className="flex items-start gap-2 mt-2 text-sm">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accent }} />
              <div>{brand.address}{brand.town ? `, ${brand.town}` : ''}{brand.pincode ? ` · ${brand.pincode}` : ''}</div>
            </div>
          </div>
        )}

        <div className="mt-10 text-center text-xs text-gray-400">
          <a href="/" className="hover:text-[#1E3A8A]">Powered by INDIVALIFE ›</a>
        </div>
      </div>

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="brand-cart-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="relative bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-lg font-black">Your cart</div>
                <div className="text-xs text-gray-500">{cart.count} item(s) · {brand.brand_name}</div>
              </div>
              <button onClick={() => setCartOpen(false)} className="text-gray-500 hover:text-black" data-testid="cart-close-btn"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              {cart.items.length === 0 && (
                <div className="text-center py-12 text-sm text-gray-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  Your cart is empty. Add products to continue.
                </div>
              )}
              {cart.items.map(it => (
                <div key={it.deal_id} className="flex gap-3 border rounded-lg p-3" data-testid={`cart-item-${it.deal_id}`}>
                  {it.image ? <img src={it.image} alt={it.title} className="h-16 w-16 object-cover rounded" /> : <div className="h-16 w-16 bg-gray-100 rounded grid place-items-center">📦</div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold line-clamp-2">{it.title}</div>
                    <div className="text-xs text-gray-500 mt-1">₹{it.price} × {it.qty} = <b style={{ color: accent }}>₹{it.price * it.qty}</b></div>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => cart.setQty(it.deal_id, it.qty - 1)} className="h-7 w-7 grid place-items-center rounded border" data-testid={`qty-dec-${it.deal_id}`}><Minus className="h-3 w-3" /></button>
                      <span className="text-sm font-bold w-6 text-center" data-testid={`qty-${it.deal_id}`}>{it.qty}</span>
                      <button onClick={() => cart.setQty(it.deal_id, it.qty + 1)} className="h-7 w-7 grid place-items-center rounded border" data-testid={`qty-inc-${it.deal_id}`}><Plus className="h-3 w-3" /></button>
                      <button onClick={() => cart.remove(it.deal_id)} className="ml-auto h-7 px-2 text-red-600 text-xs flex items-center gap-1" data-testid={`qty-remove-${it.deal_id}`}><Trash2 className="h-3 w-3" />Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {cart.items.length > 0 && (
              <div className="sticky bottom-0 bg-white border-t p-5">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="text-sm text-gray-500">Total ({cart.count} item{cart.count === 1 ? '' : 's'})</div>
                  <div className="text-2xl font-black" style={{ color: accent }}>₹{cart.total}</div>
                </div>
                <button onClick={() => setCheckout(c => ({ ...c, open: true }))} className="w-full text-white font-bold py-3 rounded-lg" style={{ background: accent }} data-testid="cart-checkout-btn">Proceed to checkout</button>
                <button onClick={cart.clear} className="w-full text-gray-500 text-xs mt-2 hover:text-red-600" data-testid="cart-clear-btn">Clear cart</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout modal */}
      {checkout.open && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60" data-testid="checkout-modal">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-black">Checkout</div>
              <button onClick={() => setCheckout(c => ({ ...c, open: false }))} className="text-gray-500"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-2">
              <input value={checkout.name} onChange={e => setCheckout(c => ({ ...c, name: e.target.value }))} placeholder="Your name *" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="checkout-name" />
              <input value={checkout.phone} onChange={e => setCheckout(c => ({ ...c, phone: e.target.value }))} placeholder="Phone *" inputMode="numeric" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="checkout-phone" />
              <textarea value={checkout.address} onChange={e => setCheckout(c => ({ ...c, address: e.target.value }))} placeholder="Delivery address (optional)" rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="checkout-address" />
              <textarea value={checkout.note} onChange={e => setCheckout(c => ({ ...c, note: e.target.value }))} placeholder="Any note for the dealer (size, color, etc.)" rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="checkout-note" />
            </div>
            <div className="mt-3 pt-3 border-t flex items-baseline justify-between">
              <div className="text-sm text-gray-500">Total ({cart.count} items)</div>
              <div className="text-xl font-black" style={{ color: accent }}>₹{cart.total}</div>
            </div>
            <button onClick={placeOrders} disabled={checkout.busy} className="w-full mt-3 text-white font-bold py-2.5 rounded-lg disabled:opacity-50" style={{ background: accent }} data-testid="checkout-place-btn">
              {checkout.busy ? 'Placing orders…' : `Place order(s) — dealer will call ${checkout.phone || 'you'}`}
            </button>
            <p className="text-[10px] text-gray-400 mt-2 text-center">No payment yet. Dealer will contact you to confirm and arrange payment.</p>
          </div>
        </div>
      )}
    </div>
  );
}
